import { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { useApiCall } from "@framework/react-hooks";
import type { ISpeedDataMarkResponse } from "@framework/types";

import type { IMultiAnalysisDataMark, IMultiAnalysisTimeRange, IStructureTreePoint } from "../../multi-analysis/types";
import { formatStructureTreePointLabel } from "../../multi-analysis/utils/structure-tree-utils";
import { normalizeTrendRangeForRequest } from "../../multi-analysis/utils/trend-series";

const EMPTY_MARKS: IMultiAnalysisDataMark[] = [];

export const useSpeedDataMarks = (
  trainId: number,
  points: readonly IStructureTreePoint[],
  globalRange: IMultiAnalysisTimeRange,
) => {
  const { formatMessage } = useIntl();
  const { start, end } = globalRange;
  const range = useMemo(() => normalizeTrendRangeForRequest({ start, end }), [start, end]);
  const pointIds = useMemo(
    () => JSON.stringify(points.map(point => point.id).sort((left, right) => left - right)),
    [points],
  );
  const requestKey = points.length && range ? JSON.stringify([trainId, pointIds, range]) : null;
  const [result, setResult] = useState<{ key: string; rows: ISpeedDataMarkResponse[] } | null>(null);
  const { fn: loadMarks } = useApiCall(
    (api, args: { trainId: number; pointIds: string; startTimestamp: string; endTimestamp: string }) =>
      api.fetchJson({ url: "/point-v2/speed/data-marks", method: "GET", data: args }) as Promise<
        ISpeedDataMarkResponse[]
      >,
    { success: false },
  );
  const loadMarksRef = useRef(loadMarks);
  useEffect(() => {
    loadMarksRef.current = loadMarks;
  }, [loadMarks]);

  useEffect(() => {
    if (!range || !requestKey) return;
    let cancelled = false;
    void loadMarksRef
      .current(undefined, { trainId, pointIds, ...range })
      .then(rows => {
        if (!cancelled) setResult({ key: requestKey, rows: rows ?? [] });
      })
      .catch(() => {
        if (!cancelled) setResult({ key: requestKey, rows: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [pointIds, range, requestKey, trainId]);

  const dataMarks = useMemo(() => {
    if (!requestKey || result?.key !== requestKey) return EMPTY_MARKS;
    const pointsById = new Map(points.map(point => [point.id, point]));
    return result.rows.map((row): IMultiAnalysisDataMark => {
      const point = pointsById.get(row.idPoint);
      return {
        key: `${row.idPoint}:${row.idMeasure}:${row.Date}`,
        idMeasure: row.idMeasure,
        idPoint: row.idPoint,
        date: row.Date,
        measureDate: row.Date,
        pointName: point
          ? formatStructureTreePointLabel(point, direction =>
              formatMessage({ id: `enums.pointDirection.${direction}` }),
            )
          : String(row.idPoint),
        measureName: row.Name,
        measureDescription: row.Description,
        value: row.Value1,
        value2: row.Value2,
        units: row.Units,
        alarmStatus: row.AlarmStatus,
        rotationFrequencyRpm: row.RPM,
        param1: row.Param1,
        param2: row.Param2,
      };
    });
  }, [formatMessage, points, requestKey, result]);

  return { dataMarks, isLoading: requestKey !== null && result?.key !== requestKey };
};
